import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseQueryOptions, UseMutationOptions } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import type { ErrorType } from "./custom-fetch";

export type ZapierEventType =
  | "quote.sent"
  | "quote.accepted"
  | "invoice.sent"
  | "invoice.paid"
  | "customer.created";

export interface ZapierEventDescriptor {
  id: ZapierEventType;
  label: string;
  description: string;
}

export interface ZapierSubscription {
  id: number;
  targetUrl: string;
  eventTypes: ZapierEventType[];
  signingSecret?: string;
  lastDeliveryStatus: string | null;
  lastDeliveryAt: string | null;
  failureCount: number;
  createdAt: string;
}

export const ZAPIER_EVENTS_QUERY_KEY = ["/api/integrations/zapier/events"] as const;
export const ZAPIER_SUBSCRIPTIONS_QUERY_KEY = ["/api/integrations/zapier/subscriptions"] as const;

export const getZapierEvents = async (): Promise<{ events: ZapierEventDescriptor[] }> =>
  customFetch<{ events: ZapierEventDescriptor[] }>("/api/integrations/zapier/events");

export const useGetZapierEvents = <
  TData = { events: ZapierEventDescriptor[] },
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<{ events: ZapierEventDescriptor[] }, TError, TData>;
}) => {
  const { query: queryOptions } = options ?? {};
  return useQuery<{ events: ZapierEventDescriptor[] }, TError, TData>({
    queryKey: queryOptions?.queryKey ?? ZAPIER_EVENTS_QUERY_KEY,
    queryFn: getZapierEvents,
    ...queryOptions,
  });
};

export const listZapierSubscriptions = async (): Promise<{ subscriptions: ZapierSubscription[] }> =>
  customFetch<{ subscriptions: ZapierSubscription[] }>("/api/integrations/zapier/subscriptions");

export const useListZapierSubscriptions = <
  TData = { subscriptions: ZapierSubscription[] },
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<{ subscriptions: ZapierSubscription[] }, TError, TData>;
}) => {
  const { query: queryOptions } = options ?? {};
  return useQuery<{ subscriptions: ZapierSubscription[] }, TError, TData>({
    queryKey: queryOptions?.queryKey ?? ZAPIER_SUBSCRIPTIONS_QUERY_KEY,
    queryFn: listZapierSubscriptions,
    ...queryOptions,
  });
};

export interface CreateZapierSubscriptionBody {
  targetUrl: string;
  eventTypes: ZapierEventType[];
}

export const createZapierSubscription = async (
  body: CreateZapierSubscriptionBody,
): Promise<{ subscription: ZapierSubscription }> =>
  customFetch<{ subscription: ZapierSubscription }>("/api/integrations/zapier/subscriptions", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

export const useCreateZapierSubscription = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    { subscription: ZapierSubscription },
    TError,
    CreateZapierSubscriptionBody,
    TContext
  >;
}) => {
  const queryClient = useQueryClient();
  const userMutation = options?.mutation;
  return useMutation<
    { subscription: ZapierSubscription },
    TError,
    CreateZapierSubscriptionBody,
    TContext
  >({
    mutationFn: async (body) => {
      const result = await createZapierSubscription(body);
      await queryClient.invalidateQueries({ queryKey: ZAPIER_SUBSCRIPTIONS_QUERY_KEY });
      return result;
    },
    ...userMutation,
  });
};

export interface UpdateZapierSubscriptionBody {
  id: number;
  targetUrl?: string;
  eventTypes?: ZapierEventType[];
}

export const updateZapierSubscription = async (
  body: UpdateZapierSubscriptionBody,
): Promise<{ subscription: ZapierSubscription }> => {
  const { id, ...rest } = body;
  return customFetch<{ subscription: ZapierSubscription }>(
    `/api/integrations/zapier/subscriptions/${id}`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(rest),
    },
  );
};

export const useUpdateZapierSubscription = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    { subscription: ZapierSubscription },
    TError,
    UpdateZapierSubscriptionBody,
    TContext
  >;
}) => {
  const queryClient = useQueryClient();
  const userMutation = options?.mutation;
  return useMutation<
    { subscription: ZapierSubscription },
    TError,
    UpdateZapierSubscriptionBody,
    TContext
  >({
    mutationFn: async (body) => {
      const result = await updateZapierSubscription(body);
      await queryClient.invalidateQueries({ queryKey: ZAPIER_SUBSCRIPTIONS_QUERY_KEY });
      return result;
    },
    ...userMutation,
  });
};

export const deleteZapierSubscription = async (id: number): Promise<{ success: boolean }> =>
  customFetch<{ success: boolean }>(`/api/integrations/zapier/subscriptions/${id}`, {
    method: "DELETE",
  });

export const useDeleteZapierSubscription = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<{ success: boolean }, TError, number, TContext>;
}) => {
  const queryClient = useQueryClient();
  const userMutation = options?.mutation;
  return useMutation<{ success: boolean }, TError, number, TContext>({
    mutationFn: async (id) => {
      const result = await deleteZapierSubscription(id);
      await queryClient.invalidateQueries({ queryKey: ZAPIER_SUBSCRIPTIONS_QUERY_KEY });
      return result;
    },
    ...userMutation,
  });
};

export interface TestZapierResponse {
  ok: boolean;
  statusCode: number | null;
  error: string | null;
}

export const testZapierSubscription = async (id: number): Promise<TestZapierResponse> =>
  customFetch<TestZapierResponse>(`/api/integrations/zapier/subscriptions/${id}/test`, {
    method: "POST",
  });

export const useTestZapierSubscription = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<TestZapierResponse, TError, number, TContext>;
}) => {
  const queryClient = useQueryClient();
  const userMutation = options?.mutation;
  return useMutation<TestZapierResponse, TError, number, TContext>({
    mutationFn: async (id) => {
      const result = await testZapierSubscription(id);
      await queryClient.invalidateQueries({ queryKey: ZAPIER_SUBSCRIPTIONS_QUERY_KEY });
      return result;
    },
    ...userMutation,
  });
};
